/* eslint-disable no-console */
import {
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
    PublicClientApplication,
} from '@azure/msal-browser'

import { CopTokenAuthConfig } from './config'
import { buildMsalConfig } from './msalConfig'

export type CopTokenResult = {
    accessToken: string
    idToken?: string
    expiresIn: number
    expiresAt: number
    tokenType?: string
    scope?: string
}

export type CopTokenError = Error & {
    errorCode?: string
    errorDescription?: string
    raw?: Record<string, unknown>
}

let msalInstance: PublicClientApplication | null = null
let initPromise: Promise<void> | null = null

const getScopes = (cfg: CopTokenAuthConfig): string[] => [`api://${cfg.backendAppId}/.default`]

const getMsalInstance = async (cfg: CopTokenAuthConfig): Promise<PublicClientApplication> => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(buildMsalConfig(cfg))
    }
    if (!initPromise) {
        initPromise = msalInstance.initialize()
    }
    await initPromise
    return msalInstance
}

const toCopTokenResult = (result: AuthenticationResult): CopTokenResult => {
    const expiresAt = result.expiresOn ? result.expiresOn.getTime() : Date.now() + 3600 * 1000
    return {
        accessToken: result.accessToken,
        idToken: result.idToken,
        expiresIn: Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)),
        expiresAt,
        tokenType: 'Bearer',
        scope: result.scopes?.join(' '),
    }
}

const toCopTokenError = (err: unknown): CopTokenError => {
    const e = err as { errorCode?: string; errorMessage?: string; message?: string }
    const error: CopTokenError = new Error(e.errorMessage || e.message || 'MSAL token acquisition failed')
    error.errorCode = e.errorCode
    error.errorDescription = e.errorMessage
    error.raw = err as Record<string, unknown>
    return error
}

// True once MSAL has a cached account — unaffected by blocked 3rd-party cookies.
export const hasMsalAccount = async (cfg: CopTokenAuthConfig): Promise<boolean> => {
    const client = await getMsalInstance(cfg)
    return client.getAllAccounts().length > 0
}

const neverSettles = (): Promise<CopTokenResult> => new Promise<CopTokenResult>(() => undefined)

type AcquireContext = {
    client: PublicClientApplication
    cfg: CopTokenAuthConfig
    scopes: string[]
}

// Try hidden ssoSilent first; fall back to a visible redirect only if that's blocked.
const acquireWithoutAccount = async ({ client, cfg, scopes }: AcquireContext): Promise<CopTokenResult> => {
    try {
        const sso = await client.ssoSilent({ scopes, loginHint: cfg.loginHint })
        if (cfg.debug) console.info('[CopTokenAuth][MSAL] Resolved token via hidden ssoSilent check.')
        return toCopTokenResult(sso)
    } catch (ssoErr) {
        if (cfg.debug) {
            console.info('[CopTokenAuth][MSAL] ssoSilent failed (e.g. blocked 3rd-party cookies) — falling back to visible redirect.', ssoErr)
        }
        await client.acquireTokenRedirect({ scopes, loginHint: cfg.loginHint })
        return neverSettles()
    }
}

// Cached account present — renew via refresh token; redirect only if it expired.
const acquireWithAccount = async ({ client, cfg, scopes }: AcquireContext, account: AccountInfo): Promise<CopTokenResult> => {
    try {
        const silent = await client.acquireTokenSilent({ scopes, account })
        if (cfg.debug) console.info('[CopTokenAuth][MSAL] Token renewed silently via refresh token.')
        return toCopTokenResult(silent)
    } catch (err) {
        if (!(err instanceof InteractionRequiredAuthError)) throw toCopTokenError(err)

        // Refresh token expired/revoked — the one documented visible reload case.
        if (cfg.debug) console.info('[CopTokenAuth][MSAL] Refresh token expired — redirecting to re-authenticate.')
        await client.acquireTokenRedirect({ scopes, account })
        return neverSettles()
    }
}

export const acquireMsalToken = async (cfg: CopTokenAuthConfig): Promise<CopTokenResult> => {
    if (!cfg.clientId || !cfg.tenantId || !cfg.backendAppId) {
        throw new Error('CopTokenAuth: clientId, tenantId, and backendAppId are required.')
    }

    const client = await getMsalInstance(cfg)
    const scopes = getScopes(cfg)

    // Resolve a return-from-redirect response first (sets the account cache).
    const redirectResult = await client.handleRedirectPromise().catch(() => null)
    if (redirectResult) {
        if (cfg.debug) console.info('[CopTokenAuth][MSAL] Resolved token via redirect response.')
        return toCopTokenResult(redirectResult)
    }

    const account: AccountInfo | undefined = client.getActiveAccount() ?? client.getAllAccounts()[0]
    const context: AcquireContext = { client, cfg, scopes }

    return account
        ? acquireWithAccount(context, account)
        : acquireWithoutAccount(context)
}

